import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, MoreVertical, Plus } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConnectorIcon } from '@/components/connectors/ConnectorIcon';
import { Connection, ConnectionType, ConnectorListItem } from '@optidev/shared-types';
import { cn } from '@/lib/utils';

interface ConnectionsListProps {
  connections: Connection[];
  connectors?: ConnectorListItem[];
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
  onRefreshToken?: (id: string) => void;
  onToggleEnabled?: (id: string, enabled: boolean) => void;
  /** "Use in chat" on a connection: opens the paste-ready prompt for it. */
  onViewUsage?: (connection: Connection) => void;
  /** "Add another" in a service's dialog: opens the setup form for that connector. */
  onAddAnother?: (connectorId: string) => void;
  /** "Rename" on a connection: saves the new name. Rejects with a message to show on failure. */
  onRename?: (id: string, name: string) => Promise<void>;
}

/**
 * One grade down from the default menu row, so the menu sits under the row rather than over it.
 * No leading icons: five of them turned a list of five short words into a busy little toolbar.
 * An in-flight action gets a trailing spinner, which keeps the labels still.
 */
const MENU_ITEM = 'px-2 py-1.5 text-[12.5px]';
const MENU_SPINNER = 'ml-auto size-3 flex-none animate-spin opacity-60';

/** Names a service row previews before the rest becomes "+N". */
const PREVIEW_NAMES = 2;

type Health = 'active' | 'paused' | 'error';

const healthOf = (connection: Connection): Health =>
  connection.enabled === false ? 'paused' : connection.status === 'error' ? 'error' : 'active';

const HEALTH_LABEL: Record<Health, string> = { active: 'Active', paused: 'Paused', error: 'Error' };
const HEALTH_DOT: Record<Health, string> = {
  active: 'bg-emerald-500',
  paused: 'bg-amber-500',
  error: 'bg-red-500',
};

/** Matches the API's bound on `connections.name` (varchar 255). */
const NAME_MAX_LENGTH = 255;

/**
 * The account a connection was made with. The API keeps it in metadata (email, then display
 * name, then shop domain — the order the backend auto-names with), so it survives a rename.
 * Connections from before user info was stored only carry it inside the name, as
 * "Google Sheets (email@example.com) #2", so that is the fallback.
 */
const accountOf = (connection: Connection): string | null => {
  const metadata = connection.metadata ?? {};
  for (const key of ['email', 'name', 'shopDomain']) {
    const value = metadata[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  const emailMatch = connection.name.match(/\(([^)]+@[^)]+)\)/);
  return emailMatch ? emailMatch[1] : null;
};

/** The name with its "(account)" taken out, since the account has its own line under it. */
const displayNameOf = (name: string, account: string | null): string => {
  if (!account) return name;
  const stripped = name.replace(`(${account})`, '').replace(/\s+/g, ' ').trim();
  return stripped || name;
};

/**
 * Your connections, one row per *service*, with the connections themselves behind "Open".
 *
 * The row answers the question the panel is there for — which services you have connected — and
 * stays the same height whether a service holds one connection or five. Every service opens the
 * same dialog, so a single-connection service is not a different kind of thing from a grouped one:
 * the dialog is where a connection's actions live, whatever the count.
 */
export default function ConnectionsList({
  connections,
  connectors = [],
  onDelete,
  onTest,
  onRefreshToken,
  onToggleEnabled,
  onViewUsage,
  onAddAnother,
  onRename,
}: ConnectionsListProps) {
  const connectorsMap = useMemo(() => new Map(connectors.map((c) => [c.id, c])), [connectors]);

  const [testingId, setTestingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Inline rename: one row at a time turns its name into an input.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [isSavingRename, setIsSavingRename] = useState(false);
  const renameInputRef = useRef<HTMLInputElement>(null);
  // Set when Rename is picked so the closing menu does not pull focus back to its (unmounting) trigger.
  const skipMenuRefocusRef = useRef(false);

  // The input mounts in the same render the menu closes in, so focus it a frame later.
  useEffect(() => {
    if (!renamingId) return;
    const frame = requestAnimationFrame(() => {
      renameInputRef.current?.focus();
      renameInputRef.current?.select();
    });
    return () => cancelAnimationFrame(frame);
  }, [renamingId]);

  const handleTest = async (id: string): Promise<void> => {
    setTestingId(id);
    try {
      await onTest(id);
    } finally {
      setTestingId(null);
    }
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteConfirmId) return;
    setDeletingId(deleteConfirmId);
    try {
      await onDelete(deleteConfirmId);
    } finally {
      setDeletingId(null);
      setDeleteConfirmId(null);
    }
  };

  const handleRefreshToken = async (id: string): Promise<void> => {
    if (!onRefreshToken) return;
    setRefreshingId(id);
    try {
      await onRefreshToken(id);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleToggleEnabled = async (id: string, currentEnabled: boolean): Promise<void> => {
    if (!onToggleEnabled) return;
    setTogglingId(id);
    try {
      await onToggleEnabled(id, !currentEnabled);
      toast.success(currentEnabled ? 'Connection paused' : 'Connection resumed');
    } catch {
      toast.error('Failed to update connection');
    } finally {
      setTogglingId(null);
    }
  };

  const handleCopyId = (id: string): void => {
    navigator.clipboard.writeText(id);
    toast.success('Connection ID copied to clipboard');
  };

  const startRename = (connection: Connection): void => {
    skipMenuRefocusRef.current = true;
    setDraftName(connection.name);
    setRenamingId(connection.id);
  };

  const cancelRename = (): void => {
    setRenamingId(null);
    setDraftName('');
  };

  const commitRename = async (connection: Connection): Promise<void> => {
    if (!onRename || isSavingRename) return;
    const name = draftName.trim();
    if (!name) return;
    if (name === connection.name) {
      cancelRename();
      return;
    }
    setIsSavingRename(true);
    try {
      await onRename(connection.id, name);
      toast.success('Connection renamed');
      cancelRename();
    } catch (error) {
      // Stay in edit mode with the typed name so the user can fix it and retry.
      toast.error(error instanceof Error && error.message ? error.message : "Couldn't rename connection");
    } finally {
      setIsSavingRename(false);
    }
  };

  const connectionToDelete = deleteConfirmId
    ? connections.find((c) => c.id === deleteConfirmId)
    : null;

  const isOAuthConnection = (connection: Connection): boolean =>
    connection.authType === ConnectionType.OAUTH ||
    connection.authType === ConnectionType.OAUTH_MACHINE ||
    (connection.authMethod?.startsWith('oauth') ?? false);

  // Being an OAuth connection is not enough to refresh: some providers issue
  // non-expiring tokens with no refresh token (GitHub OAuth Apps, Slack), and a
  // static-token auth method (PAT / API key) on an OAuth connector never has one.
  // The API reports this per connection. A missing flag is treated as refreshable
  // so an older API response doesn't hide a working action.
  const canRefreshToken = (connection: Connection): boolean =>
    isOAuthConnection(connection) && !!onRefreshToken && connection.hasRefreshToken !== false;

  /** Connections of one service, in first-appearance order so the list doesn't reshuffle. */
  const groups = useMemo(() => {
    const byConnector = new Map<string, Connection[]>();
    for (const connection of connections) {
      const key = connection.type || 'unknown';
      const existing = byConnector.get(key);
      if (existing) existing.push(connection);
      else byConnector.set(key, [connection]);
    }
    return [...byConnector.entries()].map(([type, items]) => ({ type, items }));
  }, [connections]);

  const [openType, setOpenType] = useState<string | null>(null);
  const openGroup = openType ? groups.find((group) => group.type === openType) : undefined;

  // Deleting the last connection of a service closes its dialog: the service is gone from the
  // list behind it, so there is nothing for the dialog to be showing.
  useEffect(() => {
    if (openType && !openGroup) setOpenType(null);
  }, [openType, openGroup]);

  if (connections.length === 0) {
    return null;
  }

  const serviceInfo = (type: string) => {
    const connector = connectorsMap.get(type);
    const brand = connector?.brandTheme?.primary;
    return { connector, name: connector?.name ?? type, brand };
  };

  const renderServiceRow = (type: string, items: Connection[]): React.ReactElement => {
    const { connector, name, brand } = serviceInfo(type);
    const names = items.map((item) => displayNameOf(item.name, accountOf(item)));
    const overflow = names.length - PREVIEW_NAMES;
    const preview =
      overflow > 0 ? `${names.slice(0, PREVIEW_NAMES).join(', ')} +${overflow}` : names.join(', ');
    const worst: Health = items.some((item) => healthOf(item) === 'error')
      ? 'error'
      : items.every((item) => healthOf(item) === 'paused')
        ? 'paused'
        : 'active';

    return (
      <li
        key={type}
        data-testid="connection-service"
        data-connector-id={type}
        className="flex items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5"
      >
        {/*
          The mark sits on a white tile, never on its brand colour: several marks ARE their brand
          colour (Stripe's purple S, Sheets' green), and painting the tile the same colour made
          them vanish. The brand shows as a thin ring around the tile instead.
        */}
        <span
          style={brand ? { boxShadow: `0 0 0 1.5px ${brand}` } : undefined}
          className={cn(
            'relative grid size-9 flex-none place-items-center rounded-lg bg-white p-1.5',
            !brand && 'ring-1 ring-border',
          )}
        >
          <ConnectorIcon
            connector={connector || { icon: 'Database', name }}
            className="size-full"
            // The tile is white in both themes, so the mark must be the dark variant in both.
            surface="light"
          />
          {worst !== 'active' && (
            <span
              aria-hidden="true"
              className={cn(
                'absolute -right-1 -top-1 size-2.5 rounded-full ring-2 ring-card',
                HEALTH_DOT[worst],
              )}
            />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-1.5">
            <span className="truncate text-sm font-semibold tracking-tight">{name}</span>
            {items.length > 1 && (
              <span className="flex-none text-xs tabular-nums text-muted-foreground">
                {items.length}
              </span>
            )}
          </span>
          <span className="block truncate text-xs text-muted-foreground" title={names.join(', ')}>
            {preview}
          </span>
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => setOpenType(type)}
          aria-label={`Open ${name}`}
          className="h-8 flex-none px-3 text-xs"
        >
          Open
        </Button>
      </li>
    );
  };

  /**
   * A connection inside its service's dialog: name, account, state, one primary action and the
   * rest behind "…". The primary action is whatever the connection most needs — resuming it if it
   * is paused, fixing it if it is failing, otherwise using it.
   */
  const renderConnectionRow = (connection: Connection): React.ReactElement => {
    const health = healthOf(connection);
    const isEnabled = health !== 'paused';
    const refreshable = canRefreshToken(connection);
    const account = accountOf(connection);
    const displayName = displayNameOf(connection.name, account);
    const busy =
      testingId === connection.id ||
      deletingId === connection.id ||
      refreshingId === connection.id ||
      togglingId === connection.id;

    const subline = (
      <span
        title={health === 'error' ? connection.errorMessage || undefined : undefined}
        className={cn(
          'flex min-w-0 items-center gap-1 text-xs',
          health === 'error' ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground',
        )}
      >
        {account && <span className="truncate">{account}</span>}
        {account && <span aria-hidden="true">·</span>}
        <span className="flex-none">{HEALTH_LABEL[health]}</span>
        {health === 'error' && connection.errorMessage && (
          <>
            <span aria-hidden="true">·</span>
            <span className="truncate">{connection.errorMessage}</span>
          </>
        )}
      </span>
    );

    /*
     * Renaming: the name slot becomes an input at the same size, the account line stays, and the
     * trailing slot holds Save / Cancel instead of the primary action and the menu. Enter and
     * Save commit, Escape and Cancel revert (the dialog's own Escape handler defers while a row is
     * renaming); blur does neither, since a click on Cancel would otherwise blur-save first.
     */
    if (renamingId === connection.id) {
      const trimmed = draftName.trim();
      const canSave = !!trimmed && trimmed !== connection.name && !isSavingRename;
      return (
        <li
          key={connection.id}
          data-testid="connection-row"
          data-connection-id={connection.id}
          data-renaming="true"
          className="flex items-center gap-3 border-b border-border/60 py-3.5 last:border-0"
        >
          <span
            aria-hidden="true"
            className={cn('size-2 flex-none rounded-full', HEALTH_DOT[health])}
          />
          <span className="flex min-w-0 flex-1 flex-col gap-1">
            <Input
              ref={renameInputRef}
              value={draftName}
              onChange={(event) => setDraftName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (canSave) void commitRename(connection);
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  cancelRename();
                }
              }}
              disabled={isSavingRename}
              maxLength={NAME_MAX_LENGTH}
              aria-label="Connection name"
              data-testid="connection-rename-input"
              className="h-7 max-w-sm rounded-lg px-2 text-sm font-semibold tracking-tight"
            />
            {subline}
          </span>

          {isSavingRename && (
            <Loader2 className="size-3.5 flex-none animate-spin text-muted-foreground" />
          )}
          <Button
            size="sm"
            variant="contrast"
            disabled={!canSave}
            onClick={() => commitRename(connection)}
            className="h-8 flex-none px-3 text-xs"
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={isSavingRename}
            onClick={cancelRename}
            className="h-8 flex-none px-3 text-xs"
          >
            Cancel
          </Button>
        </li>
      );
    }

    let primary: React.ReactElement | null = null;
    if (health === 'paused' && onToggleEnabled) {
      primary = (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => handleToggleEnabled(connection.id, false)}
          className="h-8 px-3 text-xs"
        >
          Resume
        </Button>
      );
    } else if (health === 'error') {
      primary = refreshable ? (
        <Button
          size="sm"
          variant="contrast"
          disabled={busy}
          onClick={() => handleRefreshToken(connection.id)}
          className="h-8 px-3 text-xs"
        >
          Refresh token
        </Button>
      ) : (
        <Button
          size="sm"
          variant="contrast"
          disabled={busy}
          onClick={() => handleTest(connection.id)}
          className="h-8 px-3 text-xs"
        >
          Test
        </Button>
      );
    } else if (onViewUsage) {
      primary = (
        <Button
          size="sm"
          variant="outline"
          onClick={() => onViewUsage(connection)}
          className="h-8 px-3 text-xs"
        >
          Use in chat
        </Button>
      );
    }

    return (
      <li
        key={connection.id}
        data-testid="connection-row"
        data-connection-id={connection.id}
        className="flex items-center gap-3 border-b border-border/60 py-3.5 last:border-0"
      >
        <span
          aria-hidden="true"
          className={cn('size-2 flex-none rounded-full', HEALTH_DOT[health])}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold tracking-tight">{displayName}</span>
          {subline}
        </span>

        {busy && <Loader2 className="size-3.5 flex-none animate-spin text-muted-foreground" />}
        {primary}

        {/*
          `modal={false}`: a modal Radix menu puts `pointer-events: none` on <body> while it is
          open and takes it off on close. Nested inside this dialog — itself modal — the two unwind
          out of order and the style is left behind, which locks the page until a reload. A small
          action menu needs neither the scroll lock nor the focus trap, so it opts out.
        */}
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`More actions for ${displayName}`}
              className="h-8 w-8 flex-none p-0 text-muted-foreground"
            >
              <MoreVertical className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[184px] p-1"
            onCloseAutoFocus={(event) => {
              if (skipMenuRefocusRef.current) {
                skipMenuRefocusRef.current = false;
                event.preventDefault();
              }
            }}
          >
            {onRename && (
              <DropdownMenuItem
                onClick={() => startRename(connection)}
                disabled={busy}
                className={MENU_ITEM}
              >
                Rename
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              onClick={() => handleTest(connection.id)}
              disabled={busy || !isEnabled}
              className={MENU_ITEM}
            >
              Test
              {testingId === connection.id && <Loader2 className={MENU_SPINNER} />}
            </DropdownMenuItem>

            <DropdownMenuItem onClick={() => handleCopyId(connection.id)} className={MENU_ITEM}>
              Copy connection ID
            </DropdownMenuItem>

            {refreshable && (
              <DropdownMenuItem
                onClick={() => handleRefreshToken(connection.id)}
                disabled={refreshingId === connection.id || !isEnabled}
                className={MENU_ITEM}
              >
                Refresh token
                {refreshingId === connection.id && <Loader2 className={MENU_SPINNER} />}
              </DropdownMenuItem>
            )}

            {onToggleEnabled && (
              <DropdownMenuItem
                onClick={() => handleToggleEnabled(connection.id, isEnabled)}
                disabled={togglingId === connection.id}
                className={MENU_ITEM}
              >
                {isEnabled ? 'Pause' : 'Resume'}
                {togglingId === connection.id && <Loader2 className={MENU_SPINNER} />}
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={() => setDeleteConfirmId(connection.id)}
              disabled={deletingId === connection.id}
              className={cn(MENU_ITEM, 'text-destructive focus:bg-destructive/10 focus:text-destructive')}
            >
              Delete
              {deletingId === connection.id && <Loader2 className={MENU_SPINNER} />}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </li>
    );
  };

  const open = openGroup ? serviceInfo(openGroup.type) : null;

  return (
    <>
      <ul className="flex flex-col gap-1.5">
        {groups.map((group) => renderServiceRow(group.type, group.items))}
      </ul>

      <Dialog
        open={!!openGroup}
        onOpenChange={(isOpen) => {
          if (isOpen) return;
          cancelRename();
          setOpenType(null);
        }}
      >
        <DialogContent
          data-testid="connection-service-dialog"
          data-connector-id={openGroup?.type}
          hideCloseButton
          // Escape while a row is renaming backs out of the rename, not the dialog.
          onEscapeKeyDown={(event) => {
            if (renamingId) {
              event.preventDefault();
              cancelRename();
            }
          }}
          /*
            A click outside does not dismiss: the dialog has a Close button and Escape, and a
            stray click on the scrim closing it mid-task (or a click inside the stacked usage
            modal counting as "outside" this one) is a worse surprise than a dialog that stays.
          */
          onInteractOutside={(event) => event.preventDefault()}
          className="flex max-h-[85vh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 p-0"
        >
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b border-border px-5 py-4 text-left">
            <span
              style={open?.brand ? { boxShadow: `0 0 0 1.5px ${open.brand}` } : undefined}
              className={cn(
                'grid size-10 flex-none place-items-center rounded-lg bg-white p-2',
                !open?.brand && 'ring-1 ring-border',
              )}
            >
              <ConnectorIcon
                connector={open?.connector || { icon: 'Database', name: open?.name ?? 'Unknown' }}
                className="size-full"
                surface="light"
              />
            </span>
            <div className="min-w-0 flex-1">
              <DialogTitle className="truncate text-base">{open?.name}</DialogTitle>
              <DialogDescription className="text-xs">
                {openGroup?.items.length === 1
                  ? '1 connection'
                  : `${openGroup?.items.length ?? 0} connections`}
              </DialogDescription>
            </div>
            {onAddAnother && openGroup && (
              <Button
                size="sm"
                variant="contrast"
                onClick={() => onAddAnother(openGroup.type)}
                className="h-8 flex-none gap-1 px-3 text-xs"
              >
                <Plus className="size-3.5" />
                Add another
              </Button>
            )}
          </DialogHeader>

          <ul className="min-h-0 flex-1 overflow-y-auto px-5">
            {openGroup?.items.map((connection) => renderConnectionRow(connection))}
          </ul>

          {/* No "×" in the header: Escape dismisses, and one labelled way out is enough. */}
          <DialogFooter className="border-t border-border px-5 py-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                cancelRename();
                setOpenType(null);
              }}
              className="h-8 px-3 text-xs"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!deleteConfirmId}
        onOpenChange={(isOpen) => !isOpen && setDeleteConfirmId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{connectionToDelete?.name}</strong>? This
              action cannot be undone. Any projects using this connection will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingId ? 'Deleting...' : 'Delete Connection'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
